<template>
  <section>
    <coozzy-card class="global-card no-top-border table-card">
      <!-- Heading -->
      <div class="row justify-content-between align-items-center">
        <div class="col-md-8">
          <h5 v-if="selectedView === 'componentsCostsList'">
            {{ componentsCount }} {{ $t('message.onBoarding.investmentPlanning.components') }}
          </h5>
          <h5 v-else-if="selectedView === 'planningCostsList'">
            {{ plansCount }} {{ $t('message.onBoarding.investmentPlanning.planningCostsTitle') }}
          </h5>
        </div>
        <template
          v-if="selectedView === 'componentsCostsList'">
          <div
            class="col-12 col-md-2">
            <coozzy-button
              v-if="isFiltered"
              class="w-100 sort"
              @click="initFilterComponents()">
              <coozzy-filter-icon />
              {{ $t('message.generic.clearFilters') }}
            </coozzy-button>
          </div>
          <div
            v-if="hasInvestmentPlanRole"
            class="col-12 col-md-2">
            <coozzy-button
              design="prop-green"
              class="w-100 sort"
              @click="readOnly ? goToEditComponent(true) : createComponentClicked()">
              {{ $t('message.onBoarding.investmentPlanning.addComponent') }}
            </coozzy-button>
          </div>
        </template>
        <template
          v-else-if="selectedView === 'planningCostsList'">
          <div
            class="col-12 col-md-2">
            <coozzy-button
              v-if="isFiltered"
              class="w-100 sort"
              @click="initFilterPlans()">
              <coozzy-filter-icon />
              {{ $t('message.generic.clearFilters') }}
            </coozzy-button>
          </div>
          <div
            v-if="hasInvestmentPlanRole"
            class="col-12 col-md-2">
            <coozzy-button
              design="prop-green"
              class="w-100 sort"
              @click="readOnly ? goToEditComponent(true) : createComponentClicked()">
              {{ $t('message.onBoarding.investmentPlanning.addPlanningCosts') }}
            </coozzy-button>
          </div>
        </template>
        <div class="col-12">
          <div class="col-12 p-0">
            <div class="row mb-2 mx-0">
              <div class="col-2 pl-0">
                <coozzy-form-radio
                  v-model="selectedView"
                  name="investmentPlanning"
                  class="p-0 btn likeBtn"
                  :default-model="selectedView"
                  :select-value="'componentsCostsList'">
                  {{ $t('message.onBoarding.investmentPlanning.componentCostsTitle') }}
                </coozzy-form-radio>
              </div>
              <div
                class="col-2 pl-0 pr-0">
                <coozzy-form-radio
                  v-model="selectedView"
                  name="investmentPlanning"
                  class="p-0 btn likeBtn"
                  :default-model="selectedView"
                  :select-value="'planningCostsList'">
                  {{ $t('message.onBoarding.investmentPlanning.planningCostsTitle') }}
                </coozzy-form-radio>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template v-if="selectedView === 'componentsCostsList'">
        <component-costs
          ref="buildingComponentCosts"
          :read-only="readOnly"
          :building="building"
          :components="listComponents"
          :buildings-list="[building]"
          :objects-list="objectsList"
          source="building"
          :filters="filters"
          @filters-changed="filtersChanged"
          @change-component="emitChangesComponents"
          @component-added="emitAddComponents"
          @delete-component="deleteComponent"
          @delete-new-component="deleteNewComponent"
          @save-data="saveData" />
      </template>
      <template v-else-if="selectedView === 'planningCostsList'">
        <planning-costs
          ref="buildingPlanningCosts"
          :read-only="readOnly"
          :list-planning-costs="listPlanningCosts"
          :buildings-list="[building]"
          :objects-list="objectsList"
          source="building"
          :filters="filters"
          @filters-changed="filtersChanged"
          @plan-added="emitAddPlans"
          @change-plan="emitChangesPlans"
          @delete-plan="deletePlan"
          @save-data="saveData" />
      </template>
    </coozzy-card>
  </section>
</template>

<script>

import { routeChecks } from '@/mixins/routeChecks'
import { tables } from '@/mixins/tables'
import { user } from '@/mixins/user'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import { componentUtils } from '@/mixins/componentUtils'
import { dateUtils } from '@/mixins/dateUtils'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyFilterIcon from '@/framework/components/icons/CoozzyFilterIcon.vue'
import { helper } from '@/types/helper'
import { onboarding } from '@/mixins/onboarding'
import Vue from 'vue'
import InvestPlanApi from '@/misc/apis/InvestPlanApi'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import PlanningCosts from '@/property/tabs/PlanningCosts.vue'
import ComponentCosts from '@/property/tabs/ComponentCosts.vue'

export default {
  name: 'BuildingInvestPlanTab',
  components: {
    PlanningCosts,
    ComponentCosts,
    CoozzyFormRadio,
    CoozzyFilterIcon,
    CoozzyButton,
    CoozzyCard
  },
  mixins: [routeChecks, tables, user, componentUtils, dateUtils, helper, onboarding],
  props: {
    readOnly: {
      type: Boolean,
      default: false
    },
    addNewComponent: {
      type: [Boolean, String],
      default: false
    },
    investPlanFilters: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      objectsList: [],
      defaultComponents: [],
      listComponents: [],
      listPlanningCosts: [],
      currentYear: new Date().getFullYear(),
      selectedView: 'componentsCostsList',
      isFiltered: false,
      filters: {},
      initLoadComponentsExecuted: false
    }
  },
  computed: {
    localBuildingValue: {
      get() {
        const value = this.building.buildingValue
        return value !== null && value !== undefined && value !== '' ? value : 0
      },
      set(value) {
        this.$store.dispatch('onboarding/updateBuildingField',
          { field: 'buildingValue', value: value, dataType: 'number' })
      }
    },
    componentsCount() {
      return this.listComponents !== null ? this.listComponents.length : 0
    },
    plansCount() {
      return this.listPlanningCosts !== null ? this.listPlanningCosts.length : 0
    }
  },
  watch: {
    components: {
      immediate: true,
      handler: function (newVal, oldVal) {
        if (newVal === oldVal) {
          return
        }
        if (newVal.length > 0) {
          this.listComponents = newVal.map(component => this.enrichComponent(component))
        } else {
          this.loadComponents()
        }
      }
    },
    planningCosts: {
      handler: function (newVal, oldVal) {
        if (newVal === oldVal) {
          return
        }
        if (newVal.length > 0) {
          this.listPlanningCosts = newVal.map(plan => ({
            ...plan,
            dateString: this.$options.filters.objectToDateInput(plan.date)
          }))
        } else {
          this.listPlanningCosts = []
        }
      }
    },
    selectedView: {
      handler: function (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          const query = {}
          query.view = newVal
          this.$emit('change-filter', JSON.stringify(query), 'investPlanFilters')
        }
      }
    }
  },
  mounted: function () {
    this.loadData()
    this.loadComponents()

    const parsedFilters = this.investPlanFilters !== '' ? JSON.parse(this.investPlanFilters) : null
    if (parsedFilters) {
      this.filters = parsedFilters
      if (parsedFilters.view === 'componentsCostsList') {
        this.selectedView = 'componentsCostsList'
        if (this.addNewComponent === true && !this.readOnly) {
          this.$nextTick(() => {
            this.$refs.buildingComponentCosts.showCreateComponentModalClicked()
          })
        }
      } else if (parsedFilters.view === 'planningCostsList') {
        this.selectedView = 'planningCostsList'
        if (this.addNewComponent === true && !this.readOnly) {
          this.$nextTick(() => {
            this.$refs.buildingPlanningCosts.showCreatePlanModalClicked()
          })
        }
      }
    }
  },
  methods: {
    filtersChanged(value, query) {
      this.isFiltered = value
      query.view = this.selectedView
      this.$emit('change-filter', JSON.stringify(query), 'investPlanFilters')
    },
    loadComponents() {
      if (this.components.length === 0 && this.initLoadComponentsExecuted) {
        this.getDefaultComponents()
      } else {
        this.listComponents = this.components.map(component => this.enrichComponent(component))
      }
      if (this.planningCosts.length === 0) {
        this.listPlanningCosts = []
      } else {
        this.listPlanningCosts = this.planningCosts
        this.listPlanningCosts.forEach(plan => {
          plan.dateString = this.$options.filters.objectToDateInput(plan.date)
        })
      }
      this.initLoadComponentsExecuted = true
    },
    async saveData() {
      this.$store.dispatch('onboarding/setLoading', true)
      this.$refs.buildingComponentCosts?.syncBuildingValueToStore?.()
      const promises = [this.$store.dispatch('onboarding/persistBuildingValue')]
      const listComponentToCreate = []
      const listComponentToEdit = []
      const listPlanToCreate = []
      const listPlanToUpdate = []

      for (const component of this.listComponents) {
        if (component.internalId) {
          listComponentToCreate.push(component)
        } else if (component.modified) {
          listComponentToEdit.push(component)
        }
      }

      for (const plan of this.listPlanningCosts) {
        if (plan.internalId) {
          listPlanToCreate.push(plan)
        } else if (plan.modified) {
          listPlanToUpdate.push(plan)
        }
      }

      if (listComponentToCreate.length > 0 || listPlanToCreate.length > 0) {
        promises.push(InvestPlanApi.createComponentBulk(listComponentToCreate, listPlanToCreate))
      }
      if (listComponentToEdit.length > 0 || listPlanToUpdate.length > 0) {
        promises.push(InvestPlanApi.updateComponentBulk(listComponentToEdit, listPlanToUpdate))
      }
      return Promise.all(promises).then(() => {
        Vue.toasted.show(this.$tc('message.successMessages.componentSaved'), { type: 'success' })
        this.$store.dispatch('onboarding/setLoading', false)
      })
    },
    createComponentClicked() {
      if (this.selectedView === 'componentsCostsList') {
        this.$refs.buildingComponentCosts.showCreateComponentModalClicked()
      } else if (this.selectedView === 'planningCostsList') {
        this.$refs.buildingPlanningCosts.showCreatePlanModalClicked()
      }
    },
    goToEditComponent(newComponent = false) {
      const query = { view: 'investmentPlanning', investPlanFilters: JSON.stringify({ view: this.selectedView }) }
      if (newComponent) {
        query.addNewComponent = newComponent
      }
      if (this.isOwnerModule) {
        this.$router.push({ name: 'OwnerOnboardingView', params: { id: this.building.id }, query: query })
      } else if (this.isAdminModule) {
        this.$router.push({ name: 'AdminOnboardingView', params: { id: this.building.id }, query: query })
      } else if (this.isAssetModule) {
        this.$router.push({ name: 'AssetOnboardingView', params: { id: this.building.id }, query: query })
      } else if (this.isAccountingModule) {
        this.$router.push({ name: 'AccountingOnboardingView', params: { id: this.building.id }, query: query })
      }
    },
    initFilterComponents() {
      this.$refs.buildingComponentCosts?.initFilter()
    },
    initFilterPlans() {
      this.$refs.propertyPlanningCosts?.initFilter()
    },
    loadData() {
      // sort the list by category translated
      if (this.allObjects?.length > 0) {
        this.objectsList = this.allObjects.sort((a, b) => {
          return this.$t('message.onBoarding.buildings.objects.category.' + a.category.toLowerCase()) > this.$t('message.onBoarding.buildings.objects.category.' + b.category.toLowerCase()) ? 1 : -1
        })
      }
    },
    getDefaultComponents() {
      this.listComponents = []
      this.defaultComponents = []
      const ownerId = this.building.ownerId
      const addedPercentage = this.getAddedPercentage()
      this.typeComponentsBuilding.forEach(type => {
        const internalId = Date.now() + Math.floor(Math.random() * 1000)
        const component = JSON.parse(JSON.stringify(helper.emptyComponent))
        component.internalId = JSON.parse(JSON.stringify('b_' + internalId))
        component.ownerId = ownerId
        component.referenceId = this.building.id
        component.type = type
        component.name = this.$t('message.onBoarding.investmentPlanning.types.' + type)
        component.bkp = this.getBkpComponent(type)
        component.lifeSpan = this.getLifeSpanComponent(type)
        component.percentage = Math.floor((this.getPercentageComponent(type) * (type !== 'COMPONENT_TYPE_SHELL_CONSTRUCTION_1' ? addedPercentage : 1)))
        component.installationDateString = ''
        component.plannedReplacementDateString = ''
        component.calculatedReplacement = ''
        component.optionalValueEnhancingComponent = false
        component.costs = Math.round(this.localBuildingValue * (parseFloat(component.percentage) / 100) * 100) / 100
        this.defaultComponents.push(component)
      })
      this.objectsList.forEach(object => {
          if (object.category === 'APARTMENT') {
            this.typeComponentsObject.forEach(type => {
              const internalId = Date.now() + Math.floor(Math.random() * 1000)
              const component = JSON.parse(JSON.stringify(helper.emptyComponent))
              component.internalId = JSON.parse(JSON.stringify('o_' + internalId))
              component.ownerId = ownerId
              component.referenceId = object.id
              component.type = type
              component.name = this.$t('message.onBoarding.investmentPlanning.types.' + type)
              component.bkp = this.getBkpComponent(type)
              component.lifeSpan = this.getLifeSpanComponent(type)
              component.percentage = Math.floor((this.getPercentageComponent(type) * addedPercentage))
              component.installationDateString = ''
              component.plannedReplacementDateString = ''
              component.calculatedReplacement = ''
              component.optionalValueEnhancingComponent = false
              component.costs = Math.round(this.localBuildingValue * (parseFloat(component.percentage) / 100) * 100) / 100
              this.defaultComponents.push(component)
            })
          }
      })
      const totalPercentage = this.defaultComponents.reduce((acc, component) => {
        return acc + parseFloat(component.percentage)
      }, 0)
      if (totalPercentage < 100) {
        const rest = 100 - totalPercentage
        this.defaultComponents.forEach(component => {
          if (component.type === 'COMPONENT_TYPE_FACADE' || component.type === 'COMPONENT_TYPE_THERMAL_INSULATION') {
            component.percentage = (component.percentage + (rest / 2))
            component.costs = Math.round((this.localBuildingValue * (parseFloat(component.percentage) / 100)) * 100) / 100
          }
        })
      }
      this.listComponents = JSON.parse(JSON.stringify(this.defaultComponents))
      this.setComponents(this.listComponents)
    },
    getAddedPercentage() {
      const totalBuildingPercentage = this.typeComponentsBuilding.reduce((acc, type) => {
        return acc + parseFloat(this.getPercentageComponent(type))
      }, 0)
      const totalObjectPercentage = this.objectsList.reduce((acc, object) => {
        if (object.category === 'APARTMENT') {
          return acc + this.typeComponentsObject.reduce((innerAcc, type) => {
            return innerAcc + parseFloat(this.getPercentageComponent(type))
          }, 0)
        }
        return acc
      }, 0)
      const totalPercentage = (totalBuildingPercentage + totalObjectPercentage) - 35
      return 65 / totalPercentage
    },
    fillOutLifeSpan(item) {
      if (item.calculatedReplacement) {
        let date = new Date(item.installationDateString)
        return item.calculatedReplacement - date.getFullYear()
      }
      return ''
    },
    fillOutCalculatedReplacement(item) {
      if (item.installationDateString) {
        let date = new Date(item.installationDateString)
        return date.getFullYear() + (item.lifeSpan ? parseInt(item.lifeSpan) : 0)
      }
      return ''
    },
    enrichComponent(component) {
      const enriched = { ...component }
      enriched.installationDateString = this.$options.filters.objectToDateInput(enriched.installationDate)
      enriched.plannedReplacementDateString = this.$options.filters.objectToDateInput(enriched.plannedReplacementDate)
      enriched.calculatedReplacement = this.fillOutCalculatedReplacement(enriched)
      if (enriched.installationDate) {
        enriched.lifeSpan = this.fillOutLifeSpan(enriched)
      }
      return enriched
    },
    emitChangesComponents(components) {
      if (Array.isArray(components)) {
        components.forEach(component => {
          if (component.modified) {
            const index = this.listComponents.findIndex(c => c.id && component.id ? c.id === component.id : c.internalId === component.internalId)
            if (index !== -1) {
              this.$set(this.listComponents, index, component)
            }
            this.updateComponent(component)
          }
        })
        return
      }
      const component = components
      const index = this.listComponents.findIndex(c => c.id && component.id ? c.id === component.id : c.internalId === component.internalId)
      if (index !== -1) {
        this.$set(this.listComponents, index, component)
      }
      if (component.modified) {
        this.updateComponent(component)
      }
    },
    emitAddComponents(refs, optionalValue = false) {
      refs.forEach(ref => {
        this.addComponent(ref, optionalValue)
      })
    },
    emitAddPlans(refs) {
      refs.forEach(ref => {
        this.addPlanningCost(ref)
      })
    },
    emitChangesPlans(plans) {
      plans.forEach(plan => {
        if (plan.modified) {
          this.updatePlanningCost(plan)
        }
      })
    },
    emitChanges(component) {
      this.updateComponent(component)
    },
    deleteNewComponent(internalIds) {
      internalIds.forEach(internalId => {
        const component = this.listComponents.find(component => component.internalId && internalId ? component.internalId === internalId : false)
        if (component) {
          this.removeComponent(component)
        }
      })
    },
    deleteComponent(ids) {
      const promises = []
      ids.forEach(id => {
        const component = this.listComponents.find(component => component.id && id ? component.id === id : component.internalId === id)
        if (component) {
          this.removeComponent(component)
        }
        promises.push(InvestPlanApi.deleteComponent(id))
      })
      if (promises.length > 0) {
        return Promise.all(promises).then(() => {
          Vue.toasted.show(this.$tc('message.successMessages.componentDeleted'), { type: 'success' })
        }).catch(e => {
          console.error(e)
          Vue.toasted.show(this.$tc('message.savingErrors.data'), { type: 'error' })
        })
      }
    },
    deletePlan(ids) {
      const promises = []
      ids.forEach(id => {
        const plan = this.listPlanningCosts.find(plan => plan.id && id ? plan.id === id : plan.internalId === id)
        if (plan) {
          this.removePlanningCost(plan)
        }
        promises.push(InvestPlanApi.deleteComponent(id))
      })
      if (promises.length > 0) {
        return Promise.all(promises).then(() => {
          Vue.toasted.show(this.$tc('message.successMessages.componentDeleted'), { type: 'success' })
        }).catch(e => {
          console.error(e)
          Vue.toasted.show(this.$tc('message.savingErrors.data'), { type: 'error' })
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.selectComponentCheckbox) > .custom-control-input {
  z-index: 10;
}
.suggestion-container-bottom {
  position: absolute !important;
  background-color: white;
  z-index: 1000;
  border: 1px solid #ddd;
  max-height: 200px;
  overflow-y: auto;
  top: 100%;
  left: 0;
  right: 0;
}

.suggestion-container-top {
  position: absolute !important;
  background-color: white;
  z-index: 1000;
  border: 1px solid #ddd;
  max-height: 200px;
  overflow-y: auto;
  bottom: 100%;
  left: 0;
  right: 0;
}
:deep(.dark_line) {
  background-color: #eee;
  td {
    background-color: #eee !important;
  }
}
:deep(.width-8) {
  width: 8%;
}
:deep(.width-15) {
  width: 15%;
}
:deep(.width-20) {
  width: 20%;
}
:deep(.optionalValue) {
  background-color: #FFF9E6;
  td {
    background-color: #FFF9E6 !important;
  }
}

:deep(.custom-radio.likeBtn) {
  input {
    display: none;
  }

  label:before {
    display: none;
  }

  label {
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    line-height: 16px;
    height: 31px;
    width: 100%;
    box-shadow: none !important;
    color: $color-text-grey-dark !important;
    background-color: $color-bg-grey-dark !important;
    border-color: $color-bg-grey-dark !important;
    display: inline-block;

    ::after {
      display: none;
    }
  }

  input:checked + label {
    color: $color-text-white !important;
    background-color: $color-primary !important;
    border-color: $color-primary !important;
  }

  input:disabled + label {
    opacity: .65;
  }
}

:deep(.custom-radio.btn .custom-control-label::after) {
  display: none;
}

.global-card-tab > :deep(.card-body) {
  padding: 0;
}
</style>
